// PLEX-6383: the lineage a classic fork's first dispatch carries, for the backend history
// writer (Tim on PLEX-6383, 2026-09-30, option 1).
//
// A classic edit, replay or discard continues under a new conversation id. That id has no
// stored turns, and the request's messages may be the compacted view, so the backend can't
// take the fork's prefix from the request. The dispatch names the source conversation and
// how many of its turns the fork keeps (fork_of, fork_base), and the backend copies those
// turns from the stored source.
import {ROLE_USER} from "./roles";

/**
 * The fork params for a dispatch that continues `sourceId` under `forkId`, keeping the
 * source's first `keptTurns` turns. `{}` when there is nothing to fork from: no source (a
 * chat never saved has no stored turns to copy), or the same id.
 */
export const forkLineage = (sourceId, forkId, keptTurns) =>
  (sourceId && forkId && sourceId !== forkId && Number.isInteger(keptTurns) && keptTurns >= 0)
    ? {forkOf: sourceId, forkBase: keptTurns}
    : {};

/**
 * A classic discard forks at once but dispatches only when the user sends the next prompt.
 * `pendingFork(...)` is what discard leaves in a ref; `takePendingFork(ref, id)` hands its
 * params to that next prompt, and only if the chat is still that fork. It always clears the
 * ref, so a fork is never named twice, and a stale one never leaks into another chat.
 */
export const pendingFork = (sourceId, forkId, keptTurns) =>
  ({forkId, lineage: forkLineage(sourceId, forkId, keptTurns)});

export const takePendingFork = (ref, conversationId) => {
  const pending = ref.current;
  ref.current = null;
  return pending && pending.forkId === conversationId ? pending.lineage : {};
};

/**
 * How a classic discard at turn `idx` ends. Before a user prompt, whose text goes back to
 * the input box, it keeps `idx` turns; through any other turn, `idx + 1`. Nothing kept is a
 * new conversation; anything kept is a fork of the one discarded from. Not `idx === 0`: a
 * conversation can start with an assistant turn (loadConversation drops turns with no
 * role), and a discard after it keeps that turn (Tim, on #89).
 */
export const discardPlan = (turns, idx) => {
  const turn = turns?.[idx];
  const isUserPrompt = turn?.role === ROLE_USER && !turn.content?.some(block => block.toolResult);
  const keepCount = isUserPrompt ? idx : idx + 1;
  return {isUserPrompt, keepCount, newConversation: keepCount === 0};
};
